/**
 * `<SlotCardImageGrid>` — paginated thumbnail grid for the
 * `image-grid` slot type.
 *
 * Per TDD: "image-grid is essentially multi-pick with thumbnails", so
 * the constraint shape (`slot.multiPick`) and the output (`string[]`
 * of image ids) match `<SlotCardMultiPick>`. The page size defaults
 * to 12 (per `slot.imageGrid.pageSize`).
 *
 * Pure pagination helpers (`paginate`, `clampPage`) are exported so
 * the parent can drive the page state without remounting the card.
 */

import { useState } from 'react';
import type {
  SlotDefinition,
  SlotImage,
} from '@volley/crucible/interview/types.js';

export interface SlotCardImageGridProps {
  slot: SlotDefinition;
  value: string[];
  onChange: (next: string[]) => void;
  prefilledUntouched: boolean;
}

const DEFAULT_PAGE_SIZE = 12;

/** Pure helper — slice the image list for the requested page (1-based). */
export function paginate(
  images: readonly SlotImage[],
  page: number,
  pageSize: number,
): SlotImage[] {
  const start = (page - 1) * pageSize;
  return images.slice(start, start + pageSize);
}

/** Pure helper — clamp a requested page into the legal range. */
export function clampPage(page: number, total: number, pageSize: number): number {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  if (page < 1) return 1;
  if (page > lastPage) return lastPage;
  return page;
}

/**
 * Pure helper — true when `src` looks like a renderer-loadable URL.
 *
 * Issue #375: hello-weekend's image-grid frontmatter shipped with
 * literal `src: "placeholder.png"` strings before the Fal.ai
 * generation step landed. The browser would render those as broken
 * `<img>` tags because the renderer process can't resolve relative
 * paths against a `file://` location it doesn't own. We treat
 * anything that isn't an explicit `game-asset://`, `file://`,
 * `data:`, `http://`, or `https://` URL as un-renderable so the tile
 * falls back to a clean label-only placeholder instead of the
 * browser's default broken-image icon.
 *
 * The slot-loader is supposed to rewrite agent-relative paths to
 * `game-asset://` URLs at parse time (see
 * `packages/crucible/src/interview/slot-loader.ts`); a `src` that
 * still looks like a bare filename here means generation didn't run
 * in the upstream template. The placeholder UX is the graceful
 * degradation path for that case.
 */
export function isRenderableImageSrc(src: string | undefined): boolean {
  if (!src) return false;
  // Only fully-qualified URLs are safe to hand to <img src>. Bare
  // filenames (`placeholder.png`) and relative paths (`./foo.png`,
  // `assets/foo.png`) resolve against the renderer document's URL —
  // which in a packaged Electron app is a `file://` path the bundle
  // doesn't own — and reliably 404 to the browser's default
  // broken-image icon. The slot-loader rewrites agent-relative
  // frontmatter paths to `game-asset://` URLs before they reach the
  // renderer, so anything else here is the un-generated path.
  return (
    src.startsWith('game-asset://') ||
    src.startsWith('file://') ||
    src.startsWith('data:') ||
    src.startsWith('http://') ||
    src.startsWith('https://')
  );
}

export function SlotCardImageGrid({
  slot,
  value,
  onChange,
  prefilledUntouched,
}: SlotCardImageGridProps): React.JSX.Element {
  const cfg = slot.imageGrid;
  const [page, setPage] = useState(1);

  if (!cfg || cfg.images.length === 0) {
    return (
      <div
        data-testid={`slot-card-image-grid-${slot.id}`}
        data-empty="true"
        className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200/80"
      >
        No images configured for this question. Skip to continue.
      </div>
    );
  }

  const pageSize = cfg.pageSize ?? DEFAULT_PAGE_SIZE;
  const total = cfg.images.length;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const visible = paginate(cfg.images, page, pageSize);

  const toggle = (id: string): void => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  const pickCfg = slot.multiPick;
  const pickHint = pickCfg
    ? pickCfg.min === pickCfg.max
      ? `Pick exactly ${pickCfg.min}`
      : `Pick ${pickCfg.min}–${pickCfg.max}`
    : 'Pick any';

  return (
    <div data-testid={`slot-card-image-grid-${slot.id}`}>
      <div className="mb-2 flex items-center justify-between">
        <p
          className="text-[10px] uppercase tracking-wide text-white/40"
          data-testid={`slot-card-image-grid-${slot.id}-constraint`}
        >
          {pickHint}
        </p>
        {lastPage > 1 && (
          <p
            className="text-[10px] uppercase tracking-wide text-white/40"
            data-testid={`slot-card-image-grid-${slot.id}-page-indicator`}
          >
            Page {page} of {lastPage}
          </p>
        )}
      </div>

      <div
        className="grid grid-cols-3 gap-2"
        data-testid={`slot-card-image-grid-${slot.id}-grid`}
      >
        {visible.map((img) => {
          const selected = value.includes(img.id);
          const renderable = isRenderableImageSrc(img.src);
          return (
            <button
              key={img.id}
              type="button"
              onClick={() => toggle(img.id)}
              data-testid={`slot-card-image-grid-${slot.id}-tile`}
              data-image-id={img.id}
              data-selected={selected ? 'true' : 'false'}
              data-image-renderable={renderable ? 'true' : 'false'}
              className={`flex flex-col items-stretch gap-1 rounded-md border bg-black/20 p-1.5 text-left transition-colors ${
                selected
                  ? 'border-crucible-accent/60 ring-1 ring-crucible-accent/40'
                  : 'border-white/10 hover:border-crucible-accent/40'
              } ${selected && prefilledUntouched ? 'opacity-60' : ''}`}
            >
              {renderable ? (
                <img
                  src={img.src}
                  alt={img.label}
                  className="aspect-square w-full rounded object-cover"
                  draggable={false}
                />
              ) : (
                // Issue #375 — clean label-only placeholder when the
                // upstream template hasn't generated thumbnail assets
                // yet. Renders the option's label centred over a
                // gradient so the user can still pick meaningfully.
                // Mirrors the aspect ratio of the real image so layout
                // doesn't shift once assets are wired up.
                <div
                  data-testid={`slot-card-image-grid-${slot.id}-placeholder`}
                  data-image-id={img.id}
                  className="flex aspect-square w-full items-center justify-center rounded bg-gradient-to-br from-white/10 to-white/5 px-1.5 text-center"
                >
                  <span className="line-clamp-3 text-[11px] font-medium text-white/70">
                    {img.label}
                  </span>
                </div>
              )}
              <span className="truncate text-xs font-medium text-white/90">
                {img.label}
              </span>
              <span className="line-clamp-2 text-[10px] text-white/50">
                {img.oneLiner}
              </span>
            </button>
          );
        })}
      </div>

      {lastPage > 1 && (
        <div className="mt-2 flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => setPage((p) => clampPage(p - 1, total, pageSize))}
            disabled={page === 1}
            data-testid={`slot-card-image-grid-${slot.id}-prev`}
            className="rounded border border-white/10 bg-white/5 px-2 py-0.5 text-xs text-white/60 transition-colors hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => clampPage(p + 1, total, pageSize))}
            disabled={page === lastPage}
            data-testid={`slot-card-image-grid-${slot.id}-next`}
            className="rounded border border-white/10 bg-white/5 px-2 py-0.5 text-xs text-white/60 transition-colors hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
